/**
 * Small Hub building blocks: stat tile, service status badge, pager and
 * empty state. All token-driven so they work in light and dark mode.
 */
import type * as React from 'react';
import { ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { Button, Skeleton, cn } from '@finmin/ui';
import type { ChannelPartnerHubStatus, ChannelPartnerService } from '@/api/channel-partners';
import { serviceStateLabel, serviceStatusLabel } from '../format';

export function HubStatTile({
  label,
  value,
  hint,
  icon: Icon,
  isLoading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon: LucideIcon;
  isLoading?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
      {isLoading ? (
        <Skeleton className="mt-2 h-8 w-20" />
      ) : (
        <p className="mt-1 font-display text-3xl font-semibold tabular-nums tracking-tight text-foreground">
          {value}
        </p>
      )}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const HUB_STATUS_TONE: Record<ChannelPartnerHubStatus, string> = {
  pending: 'border-warning/30 bg-warning/10 text-warning',
  ongoing: 'border-accent/30 bg-accent/10 text-accent',
  completed: 'border-success/30 bg-success/10 text-success',
  cancelled: 'border-destructive/30 bg-destructive/10 text-destructive',
};

/**
 * The Hub group when the state is mapped (with the actual state underneath),
 * otherwise the actual state on a neutral badge.
 */
export function ServiceStatusBadge({
  service,
}: {
  service: Pick<ChannelPartnerService, 'hub_status' | 'status'>;
}) {
  const label = serviceStatusLabel(service);
  const actual = serviceStateLabel(service.status);
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span
        className={cn(
          'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
          service.hub_status
            ? HUB_STATUS_TONE[service.hub_status]
            : 'border-border bg-muted text-muted-foreground',
        )}
      >
        {label}
      </span>
      {service.hub_status && actual !== label && (
        <span className="text-[11px] text-muted-foreground">{actual}</span>
      )}
    </span>
  );
}

export function HubPager({
  page,
  pageSize,
  total,
  isFetching,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
}) {
  if (total <= pageSize && page === 1) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between gap-4 pt-3 text-sm text-muted-foreground">
      <span>
        Page {page} of {totalPages} · {total} total
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || isFetching}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden /> Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages || isFetching}
          onClick={() => onPageChange(page + 1)}
        >
          Next <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

export function HubEmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground">
        <Icon className="h-6 w-6" aria-hidden />
      </span>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
